import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { QuickBooksLinkedService, quickBooksReservedKeys } from 'app/model/linkedservice/quick-books.linkedservice';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { CONNECTOR_VERSION20 } from 'app/services/globals';
import { driverVersions } from 'app/registries/consts/tabular-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(QuickBooksLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            version: '*',
            typeProperties: {
                endpoint: '*',
                companyId: '*',
                consumerKey: '*',
                consumerSecret: 'secret',
                accessToken: 'secret',
                accessTokenSecret: 'secret',
                refreshToken: 'secret',
                useEncryptedEndpoints: '*',
                encryptedCredential: '*',
                connectionProperties: {
                    endpoint: '*',
                    companyId: '*',
                    consumerKey: '*',
                    consumerSecret: 'secret',
                    refreshToken: 'secret',
                    useEncryptedEndpoints: '*',
                    encryptedCredential: '*'
                }
            }
        }
    }
})
export class QuickBooksLinkedServiceAdapter extends LinkedServiceAdapter<QuickBooksLinkedService> {
    protected modelConstructor(): new () => QuickBooksLinkedService {
        return QuickBooksLinkedService;
    }

    protected model(): IModel {
        return QuickBooksLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: QuickBooksLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.QuickBooksLinkedService;
        properties.type = Metamodel.modelFor(QuickBooksLinkedService).name;

        if (linkedService.version === driverVersions.v2) {
            properties.version = CONNECTOR_VERSION20;
            properties.typeProperties = this._commonToResource(linkedService);
            properties.typeProperties.refreshToken = passwordOrAKVtoSecretBase(linkedService.refreshToken, this.context);
        } else if (linkedService.isOldResource) {
            properties.typeProperties = this._commonToResource(linkedService);
            properties.typeProperties.useEncryptedEndpoints = linkedService.useEncryptedEndpoints;
            properties.typeProperties.accessToken = passwordOrAKVtoSecretBase(linkedService.accessToken, this.context);
            properties.typeProperties.accessTokenSecret = passwordOrAKVtoSecretBase(linkedService.accessTokenSecret, this.context);
        } else {
            properties.typeProperties = {
                connectionProperties: this._commonToResource(linkedService)
            };
            properties.typeProperties.connectionProperties.refreshToken = passwordOrAKVtoSecretBase(linkedService.refreshToken, this.context);
            properties.typeProperties.connectionProperties.useEncryptedEndpoints = linkedService.useEncryptedEndpoints;
            if (!arrayIsNullOrEmpty(linkedService.additionalFields)) {
                linkedService.additionalFields.forEach((field) => {
                    properties.typeProperties.connectionProperties[field.key] = field.value;
                });
            }
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: QuickBooksLinkedService): Promise<QuickBooksLinkedService> {
        const properties = linkedServiceResource.properties as Model.QuickBooksLinkedService;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const typeProperties: Model.QuickBooksLinkedServiceTypeProperties = properties.typeProperties;
        linkedService.version = properties.version === CONNECTOR_VERSION20 || __IS_FABRIC_DPE__ ? driverVersions.v2 : driverVersions.v1;
        linkedService.isLegacyConnector = linkedService.version !== driverVersions.v2;

        if (properties.version === CONNECTOR_VERSION20) {
            linkedService.isOldResource = false;
            this._commonToModel(linkedService, typeProperties);
            linkedService.refreshToken = secretBaseToPasswordOrAKV(typeProperties.refreshToken, true, this.context);
        } else if (typeProperties.connectionProperties) {
            linkedService.isOldResource = false;
            const connectionProperties = typeProperties.connectionProperties;
            this._commonToModel(linkedService, connectionProperties);
            linkedService.refreshToken = secretBaseToPasswordOrAKV(connectionProperties.refreshToken, true, this.context);
            linkedService.useEncryptedEndpoints = connectionProperties.useEncryptedEndpoints;
            linkedService.additionalFields = [];
            Object.keys(connectionProperties).forEach((key) => {
                if (quickBooksReservedKeys.indexOf(key) < 0) {
                    linkedService.additionalFields.push({ key, value: connectionProperties[key] });
                    delete connectionProperties[key];
                }
            });
        } else {
            linkedService.isOldResource = true;
            this._commonToModel(linkedService, typeProperties);
            linkedService.accessToken = secretBaseToPasswordOrAKV(properties.accessToken, true, this.context);
            linkedService.accessTokenSecret = secretBaseToPasswordOrAKV(properties.accessTokenSecret, true, this.context);
            linkedService.useEncryptedEndpoints = properties.useEncryptedEndpoints;
        }

        return linkedService;
    }

    private _commonToResource(linkedService: QuickBooksLinkedService): Model.QuickBooksLinkedServiceTypeProperties {
        return {
            endpoint: linkedService.endpoint,
            companyId: linkedService.companyId,
            consumerKey: linkedService.consumerKey,
            consumerSecret: passwordOrAKVtoSecretBase(linkedService.consumerSecret, this.context)
        };
    }

    private _commonToModel(linkedService: QuickBooksLinkedService, properties: Model.QuickBooksLinkedServiceTypeProperties): void {
        linkedService.endpoint = properties.endpoint;
        linkedService.companyId = properties.companyId;
        linkedService.consumerKey = properties.consumerKey;
        linkedService.consumerSecret = secretBaseToPasswordOrAKV(properties.consumerSecret, true, this.context);
    }
}
